import message from "@/utils/globalMessage";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Space, Tag, Tooltip, Badge } from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  ThunderboltOutlined, ReloadOutlined,
} from "@ant-design/icons";
import Popconfirm from "@components/Feedback/Popconfirm";
import SearchLookup from "@components/SearchLookup";
import ResponsiveTable from "@components/ResponsiveTable";
import { api } from "@redux/api";
import { useSelector } from "react-redux";
import { useEventBus, EventName } from "@/hooks/useEventBus";
import moment from "moment";
import { formatPMScheduleRuleSummary } from "../pmScheduleUtils";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import "./PMPlan.css";

const STATUS_COLOR = {
  true: "green",
  false: "default",
};

const PM_PLAN_CONFIRM_POPUP_PROPS = {
  getPopupContainer: () => document.body,
  overlayStyle: { zIndex: 1500 },
  placement: "left",
};

const PM_PLAN_MOBILE_EXCLUDE_KEYS = [
  "name",
  "is_active",
  "equipment_count",
  "next_schedule_date",
];

const PM_PLAN_MOBILE_INCLUDE_KEYS = [
  "schedule",
  "cost_budget",
];

const PMPlan = () => {
  const navigate = useNavigate();
  const facilityId = useSelector((state) => state.Facility?.selected?.Id);
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const detailPath = (id) => buildFacilityPath(`pm-plan-detail/${id}`, facilityContext) || "/switch-view";
  const createPath = buildFacilityPath("create-pm-plan", facilityContext) || "/switch-view";
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchText, setSearchText] = useState("");
  const [triggering, setTriggering] = useState(null);
  const [ordering, setOrdering] = useState("-created_date");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (facilityId) params.append("facility", facilityId);
      if (searchText) params.append("search", searchText);
      if (ordering) params.append("ordering", ordering);
      const res = await api.filterPMPlan(params.toString());
      if (res.status === 200) {
        setData(res.data.results || []);
        setTotal(res.data.count || 0);
      }
    } catch {
      message.error("Failed to load Preventive Maintenance");
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, facilityId, searchText, ordering]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEventBus(EventName.REFRESH_PM_PLAN_LIST, fetchData);

  const handleSearchLookup = useCallback(async (keyword) => {
    try {
      const params = new URLSearchParams({ page: 1, page_size: 15 });
      if (facilityId) params.append("facility", facilityId);
      if (keyword) params.append("search", keyword);
      const res = await api.filterPMPlan(params.toString());
      if (res.status === 200) {
        return {
          data: res.data.results || [],
          success: true,
          total: res.data.count || 0,
        };
      }
    } catch {
      // lookup suggestions are optional; table fetch handles errors
    }
    return { data: [] };
  }, [facilityId]);

  const handleSearchSelect = useCallback((value) => {
    setSearchText(value || "");
    setPage(1);
  }, []);

  const handleDelete = async (id) => {
    try {
      await api.deletePMPlan(id);
      message.success("Deleted successfully");
      fetchData();
    } catch {
      message.error("Failed to delete");
    }
  };

  const handleTrigger = async (id) => {
    try {
      setTriggering(id);
      const res = await api.triggerPMPlan(id);
      if (res.status === 201) {
        message.success(`Preventive Maintenance triggered! Workorder Wrapper #${res.data.id} created.`);
      }
    } catch {
      message.error("Failed to trigger Preventive Maintenance");
    } finally {
      setTriggering(null);
    }
  };

  const renderMobileHeader = (record) => {
    const equipmentNames = Array.isArray(record.equipment_names) ? record.equipment_names : [];
    const equipmentLabel = equipmentNames.length > 0
      ? `${equipmentNames.length} equipment(s)`
      : "0 equipment(s)";
    const nextScheduleDate = record.next_schedule_date || "-";
    const isOverdue = record.next_schedule_date && moment(record.next_schedule_date).isBefore(moment(), "day");

    return (
      <div className="pm-plan-mobile-card">
        <div className="pm-plan-mobile-card-main">
          <span className="pm-plan-mobile-card-equipment" title={equipmentNames.join(", ")}>
            {equipmentLabel}
          </span>
          <span
            className="pm-plan-mobile-card-title pm-plan-mobile-card-title-link"
            title={record.name || ""}
            onClick={(event) => {
              event.stopPropagation();
              navigate(detailPath(record.id));
            }}
          >
            {record.name || "-"}
          </span>
          <span className="pm-plan-mobile-card-date">
            Next: <span className={isOverdue ? "pm-plan-mobile-card-date-overdue" : ""}>{nextScheduleDate}</span>
          </span>
        </div>
        <div className="pm-plan-mobile-card-summary">
          <span className="pm-plan-mobile-card-meta">
            <span className="pm-plan-mobile-card-meta-label">Status: </span>
            <span className="pm-plan-mobile-card-meta-value">{record.is_active ? "Active" : "Inactive"}</span>
          </span>
          {record.cycle_type ? (
            <span className="pm-plan-mobile-card-meta">
              <span className="pm-plan-mobile-card-meta-label">Schedule: </span>
              <span className="pm-plan-mobile-card-meta-value">{formatPMScheduleRuleSummary(record)}</span>
            </span>
          ) : null}
        </div>
      </div>
    );
  };


  const columns = [
    {
      title: "Plan Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: ordering === "name" ? "ascend" : ordering === "-name" ? "descend" : null,
      render: (text, record) => (
        <a onClick={() => navigate(detailPath(record.id))}>{text}</a>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 80,
      render: (v) => (
        <Badge
          status={v ? "processing" : "default"}
          text={v ? "Active" : "Inactive"}
        />
      ),
    },
    {
      title: "Schedule",
      key: "schedule",
      render: (_, record) => (
        <span>
          {record.cycle_type ? (
            <Tag color="cyan">{formatPMScheduleRuleSummary(record)}</Tag>
          ) : (
            "-"
          )}
        </span>
      ),
    },
    {
      title: "Equipment Count",
      dataIndex: "equipment_names",
      key: "equipment_count",
      render: (names) => (
        <Tag color="blue">{Array.isArray(names) ? names.length : 0} equipment(s)</Tag>
      ),
    },
    {
      title: "Next schedule date",
      key: "next_schedule_date",
      render: (_, record) => {
        const nextScheduleDate = record.next_schedule_date;
        if (!nextScheduleDate) return "-";
        const isOverdue = moment(nextScheduleDate).isBefore(moment(), "day");
        return <Tag color={isOverdue ? "red" : "blue"}>{nextScheduleDate}</Tag>;
      },
    },
    {
      title: "Budget",
      dataIndex: "cost_budget",
      key: "cost_budget",
      sorter: true,
      sortOrder: ordering === "cost_budget" ? "ascend" : ordering === "-cost_budget" ? "descend" : null,
      render: (v) => (v ? `$${parseFloat(v).toFixed(2)}` : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      valueType: "option",
      width: 130,
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => navigate(detailPath(record.id))}
            />
          </Tooltip>
          <Tooltip title="Trigger Now (generate workorders)">
            <Popconfirm
              title="Manually trigger this Preventive Maintenance? This will generate a Workorder Wrapper immediately."
              onConfirm={() => handleTrigger(record.id)}
              {...PM_PLAN_CONFIRM_POPUP_PROPS}
            >
              <Button
                size="small"
                icon={<ThunderboltOutlined />}
                loading={triggering === record.id}
                style={{ color: "#f59e0b", borderColor: "#f59e0b" }}
              />
            </Popconfirm>
          </Tooltip>
          <Popconfirm
            title="Delete this Preventive Maintenance?"
            onConfirm={() => handleDelete(record.id)}
            {...PM_PLAN_CONFIRM_POPUP_PROPS}
          >
            <Tooltip title="Delete">
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="editable">
      <div className="table-page-header--root -mx-3 items-center w-full">
        <div className="table-page-header-toolbar-band max-w-full px-3 flex flex-nowrap items-center justify-start min-w-0 mt-2">
          <div className="flex table-page-header-main-row table-page-header-main-row--split filter-item justify-between w-full min-w-0 items-center">
            <div className="table-page-header-filter-aux flex flex-nowrap items-center justify-start gap-3 min-w-0 flex-1">
              <div className="table-page-header-filter-row">
                <SearchLookup
                  name="name"
                  placeholder="Plan name"
                  defaultValue={searchText}
                  style={{ width: "20rem" }}
                  SelectedLookup_CommonRequest={handleSearchSelect}
                  Lookup_CommonupRequest={handleSearchLookup}
                />
              </div>
            </div>
            <div className="flex items-center table-page-header-actions">
              <Space size={8} align="center" wrap={false} className="table-page-header-icon-group">
                <Tooltip title="Refresh list">
                  <Button
                    type="default"
                    shape="circle"
                    className="table-page-header-icon-btn reload-refresh-btn"
                    icon={<ReloadOutlined />}
                    onClick={fetchData}
                    loading={loading}
                    aria-label="Refresh list"
                  />
                </Tooltip>
                <Tooltip title="New Preventive Maintenance">
                  <Button
                    type="default"
                    shape="circle"
                    className="table-page-header-icon-btn"
                    icon={<PlusOutlined />}
                    onClick={() => navigate(createPath)}
                    aria-label="New Preventive Maintenance"
                  />
                </Tooltip>
              </Space>
            </div>
          </div>
        </div>
      </div>
      <div className="px-3">
        <ResponsiveTable
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 900 }}
          onChange={(_, __, sorter, extra) => {
            if (extra?.action === "sort" && sorter?.field) {
              const dir = sorter.order === "ascend" ? "" : "-";
              setOrdering(`${dir}${sorter.field}`);
              setPage(1);
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `Total ${t} records`,
            onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          }}
          hideMobileTableExpand={true}
          mobileCardHeaderRender={renderMobileHeader}
          mobileExpandedExcludeKeys={PM_PLAN_MOBILE_EXCLUDE_KEYS}
          mobileExpandedIncludeKeys={PM_PLAN_MOBILE_INCLUDE_KEYS}
        />
      </div>
    </div>
  );
};

export default PMPlan;
